import type { Box } from "@patent-studio/api-types";

/** Pixels of room between a marked passage's words and the box around them. */
const ROOM = 6;

/** Why a passage is marked: referenced by the grounding, or cited and awaiting the attorney's word. */
export type HighlightTone = "referenced" | "cited";

/** A box to draw over a page, as ratios of that page's width and height. */
export interface Highlight {
  /** The producer's identity for the mark; two findings on one passage share it or not as the producer says. */
  id: string;
  page: number;
  box: Box;
  tone: HighlightTone;
  label: string;
}

/** How one tone presents itself: what it says, and the classes of a mark and its caption. */
export interface Tone {
  /** What a mark says of itself, over the box and to a screen reader. */
  label: string;
  /** The border and fill of a mark, on a page and in text alike. */
  markClass: string;
  captionClass: string;
}

/**
 * One record per tone holding every presentation, as the element tags do,
 * so the box on a page, its caption and a passage shown as text cannot drift.
 */
export const TONES: Record<HighlightTone, Tone> = {
  referenced: {
    label: "Referenced passage",
    markClass: "border-emerald-500 bg-emerald-400/25",
    captionClass: "bg-emerald-600",
  },
  cited: {
    label: "Cited, needs your confirmation",
    markClass: "border-violet-500 bg-violet-400/25",
    captionClass: "bg-violet-600",
  },
};

/**
 * The boxes over one page. A box is a ratio of the page, and the layer fills
 * the page's container, so a percentage is the ratio times the rendered
 * size with no coordinate conversion and nothing asked of the server.
 */
export function HighlightLayer({ highlights }: { highlights: Highlight[] }) {
  if (highlights.length === 0) return null;
  return (
    <div className="pointer-events-none absolute inset-0">
      {highlights.map((h) => (
        <mark
          key={h.id}
          aria-label={h.label}
          data-tone={h.tone}
          className={`absolute block rounded-sm border-2 ${TONES[h.tone].markClass}`}
          style={{
            left: `${String(h.box.left * 100)}%`,
            top: `${String(h.box.top * 100)}%`,
            width: `${String(h.box.width * 100)}%`,
            height: `${String(h.box.height * 100)}%`,
            // Room around the words. The record's box is the content box;
            // the padding grows outward and the margin pulls the whole back
            // by the same amount, so the words sit where they were and the
            // border stands clear of them.
            boxSizing: "content-box",
            padding: `${String(ROOM)}px`,
            margin: `${String(-ROOM)}px`,
          }}
        >
          {/* The caption sits on the box's top edge, so it names the mark
              without covering the words it marks. */}
          <span
            className={`absolute -top-4 left-0 whitespace-nowrap rounded-t-sm px-1 py-px text-[8px] font-bold uppercase leading-[14px] tracking-[0.06em] text-white ${TONES[h.tone].captionClass}`}
          >
            {h.label}
          </span>
        </mark>
      ))}
    </div>
  );
}
